import {
	forwardRef,
	useCallback,
	useLayoutEffect,
	useRef,
	useState,
} from "react"
import {
	IconChevronDownOutline18 as ChevronDown,
	IconChevronUpOutline18 as ChevronUp,
} from "nucleo-ui-outline-18"

import type { TextareaProps } from "@productlane/ui"
import { cn } from "@productlane/lib/src/cn"
import { Button, Textarea } from "@productlane/ui"

import { COLLAPSED_PROMPT_ROWS, exceedsCollapsedRows } from "./collapsed-prompt"

export const CollapsiblePromptTextarea = forwardRef<
	HTMLTextAreaElement,
	TextareaProps
>(({ inputClassName, variant, ...props }, forwardedRef) => {
	const [expanded, setExpanded] = useState(false)
	const [overflows, setOverflows] = useState(false)
	const innerRef = useRef<HTMLTextAreaElement | null>(null)

	const setRef = useCallback(
		(el: HTMLTextAreaElement | null) => {
			innerRef.current = el
			if (typeof forwardedRef === "function") forwardedRef(el)
			else if (forwardedRef) forwardedRef.current = el
		},
		[forwardedRef],
	)

	// react-hook-form writes the value straight to the DOM, so a value-keyed
	// effect would miss a reset that hydrates from the server.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useLayoutEffect(() => {
		const el = innerRef.current
		if (!el) return
		setOverflows(exceedsCollapsedRows(el))
	})

	return (
		<div className="relative">
			<Textarea
				{...props}
				variant={variant}
				ref={setRef}
				inputClassName={cn(overflows && "pb-11", inputClassName)}
				maxRows={expanded ? undefined : COLLAPSED_PROMPT_ROWS}
			/>
			{overflows && (
				<div
					className={cn(
						"pointer-events-none absolute inset-x-px bottom-px flex items-end justify-center bg-gradient-to-t from-white via-white/90 to-transparent pb-2 dark:from-gray-850 dark:via-gray-850/90",
						variant === "settings" ? "rounded-b-[10px]" : "rounded-b-md",
						expanded ? "h-14" : "h-20",
					)}
				>
					<Button
						className="pointer-events-auto"
						variant="outline"
						size="sm"
						Icon={expanded ? ChevronUp : ChevronDown}
						onClick={() => setExpanded((v) => !v)}
					>
						{expanded ? "Collapse" : "Expand"}
					</Button>
				</div>
			)}
		</div>
	)
})
